import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RewardAdDialog } from "@/components/RewardAd";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, ScanLine, Loader2, MapPin, X, Sparkles, RefreshCw, Zap } from "lucide-react";

const TYPE_COLORS = {
  plastic: "text-jixxeo-blue", glass: "text-cyan-400", metal: "text-gray-300",
  paper: "text-yellow-300", organic: "text-jixxeo-green", "e-waste": "text-jixxeo-purple",
  cigarette: "text-orange-400", other: "text-white",
};

export default function Scan() {
  const { applyUser } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [coords, setCoords] = useState(null);
  const [ad, setAd] = useState(null);

  const startCamera = async () => {
    setCamError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setCamReady(true);
    } catch {
      setCamError(true);
    }
  };

  useEffect(() => {
    startCamera();
    navigator.geolocation?.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setCoords(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
    return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const capture = async () => {
    if (scanning) return;
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas || !camReady) { toast.error("Camera not ready"); return; }
    const w = video.videoWidth || 720, h = video.videoHeight || 960;
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    const image = canvas.toDataURL("image/jpeg", 0.8);
    setScanning(true);
    try {
      const { data } = await api.post("/scan", { image, lat: coords?.lat, lng: coords?.lng });
      if (!data.success) {
        toast.error(data.message || "No litter detected");
        setScanning(false);
        return;
      }
      applyUser(data.user);
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setScanning(false);
    }
  };

  const doubleReward = () => {
    setAd({ label: "Double Rewards", onComplete: async () => {
      setAd(null);
      try {
        const { data } = await api.post("/rewards/ad", { placement: "double_rewards" });
        applyUser(data.user);
        toast.success(`Doubled! +${data.reward.coins} coins, +${data.reward.xp} XP`);
        setResult(null);
      } catch (e) { toast.error(apiError(e)); }
    }});
  };

  return (
    <div className="space-y-4">
      {ad && <RewardAdDialog open onClose={() => setAd(null)} onComplete={ad.onComplete} label={ad.label} />}
      <div className="flex items-center justify-between">
        <h2 className="font-head font-extrabold text-xl">Scan Litter</h2>
        <span className={`text-xs flex items-center gap-1 ${coords ? "text-jixxeo-green" : "text-muted-foreground"}`} data-testid="gps-status">
          <MapPin className="w-3.5 h-3.5" />{coords ? "GPS verified" : "GPS off"}
        </span>
      </div>

      <div className="relative aspect-[3/4] max-h-[64vh] mx-auto w-full rounded-3xl overflow-hidden border border-white/10 bg-black">
        <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="scan-video" />
        <canvas ref={canvasRef} className="hidden" />

        {/* crosshair overlay */}
        {camReady && !camError && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-8 border-2 border-jixxeo-green/50 rounded-2xl" />
            <div className="absolute top-8 left-8 w-8 h-8 border-t-4 border-l-4 border-jixxeo-green rounded-tl-2xl" />
            <div className="absolute top-8 right-8 w-8 h-8 border-t-4 border-r-4 border-jixxeo-green rounded-tr-2xl" />
            <div className="absolute bottom-8 left-8 w-8 h-8 border-b-4 border-l-4 border-jixxeo-green rounded-bl-2xl" />
            <div className="absolute bottom-8 right-8 w-8 h-8 border-b-4 border-r-4 border-jixxeo-green rounded-br-2xl" />
            {scanning && <div className="scan-line" />}
          </div>
        )}

        {scanning && (
          <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3" data-testid="scanning-overlay">
            <Loader2 className="w-10 h-10 text-jixxeo-green animate-spin" />
            <p className="font-display text-jixxeo-green text-glow-green">JIXXEO AI ANALYZING…</p>
          </div>
        )}

        {camError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <Camera className="w-12 h-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Camera access is required to scan litter. Allow permission and retry.</p>
            <Button onClick={startCamera} data-testid="retry-camera" className="rounded-full bg-jixxeo-green text-black font-bold"><RefreshCw className="w-4 h-4 mr-2" />Retry Camera</Button>
          </div>
        )}
      </div>

      <Button onClick={capture} disabled={scanning || camError} data-testid="capture-btn"
        className="w-full h-14 rounded-full bg-jixxeo-green text-black font-display text-lg hover:bg-jixxeo-greenDark neon-green disabled:opacity-40">
        <ScanLine className="w-6 h-6 mr-2" />{scanning ? "SCANNING…" : "SCAN NOW"}
      </Button>
      <p className="text-center text-xs text-muted-foreground">Point at real litter, then pick it up. Jixxeo AI verifies each cleanup.</p>

      {result && <ResultCard result={result} onClose={() => setResult(null)} onDouble={doubleReward} />}
    </div>
  );
}

function ResultCard({ result, onClose, onDouble }) {
  const d = result.detection;
  const color = TYPE_COLORS[d.type] || "text-white";
  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-6" onClick={onClose} data-testid="scan-result">
      <div className="glass rounded-3xl p-6 max-w-sm w-full border border-jixxeo-green/40 neon-green" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="float-right text-white/50 hover:text-white" data-testid="result-close"><X className="w-5 h-5" /></button>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-jixxeo-green/20 flex items-center justify-center"><Sparkles className="w-9 h-9 text-jixxeo-green" /></div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mt-3">Jixxeo AI detected</p>
          <h3 className={`font-display text-2xl mt-1 ${color}`}>{d.item}</h3>
          <p className="text-xs text-muted-foreground">Type: {d.type} · {(d.confidence * 100).toFixed(0)}% confidence {result.verified && "· GPS ✓"}</p>
          <p className="text-sm text-white/80 mt-3">{d.message}</p>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="glass rounded-2xl p-3"><p className="font-display text-2xl text-jixxeo-green">+{result.reward.xp}</p><p className="text-[10px] uppercase text-muted-foreground">XP</p></div>
            <div className="glass rounded-2xl p-3"><p className="font-display text-2xl text-jixxeo-gold">+{result.reward.coins}</p><p className="text-[10px] uppercase text-muted-foreground">Coins</p></div>
          </div>
          <Button onClick={onDouble} data-testid="double-reward-btn" className="w-full mt-4 rounded-full bg-jixxeo-gold text-black font-bold hover:bg-yellow-400 neon-gold">
            <Zap className="w-4 h-4 mr-2" />Watch Ad · Double Rewards
          </Button>
          <button onClick={onClose} className="w-full mt-2 text-sm text-muted-foreground" data-testid="result-continue">Continue hunting</button>
        </div>
      </div>
    </div>
  );
}
